
export function erf(x) {
  // save the sign of x
  var sign = (x >= 0) ? 1 : -1;
  x = Math.abs(x);

  // A&S formula 7.1.26
  var a1 =  0.254829592;
  var a2 = -0.284496736;
  var a3 =  1.421413741;
  var a4 = -1.453152027;
  var a5 =  1.061405429;
  var p  =  0.3275911;

  // A&S formula 7.1.26
  var t = 1.0/(1.0 + p*x);
  var y = 1.0 - (((((a5*t + a4)*t) + a3)*t + a2)*t + a1)*t*Math.exp(-x*x);
  return sign*y; // erf(-x) = -erf(x);
}

// Cumulative distribution function for the standard normal distribution
export function cdf(x) {
  // Approximation for cumulative standard normal distribution. [1]
  return (1.0 + erf(x / Math.sqrt(2.0))) / 2.0;
}

/**
 * Calculates the theoretical price of a European call option using the Black-Scholes formula.
 * @param {number} S - Current price of the underlying asset.
 * @param {number} K - Strike price of the option.
 * @param {number} T - Time to expiration of the option (in years).
 * @param {number} r - Risk-free interest rate (annualized, as a decimal).
 * @param {number} sigma - Volatility of the underlying asset (annualized, as a decimal).
 * @param {number} q - Dividend yield (annualized, as a decimal).
 * @returns {number} The theoretical price of the call option.
 */
export function blackScholesCall(S, K, T, r, sigma, q) {
  if (T === 0) {
    return Math.max(0, S - K);
  }
  const d1 = (Math.log(S / K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  const N_d1 = cdf(d1);
  const N_d2 = cdf(d2);

  const callPrice = S * Math.exp(-q * T) * N_d1 - K * Math.exp(-r * T) * N_d2;
  return callPrice;
}

/**
 * Calculates the theoretical price of a European put option using the Black-Scholes formula.
 * @param {number} S - Current price of the underlying asset.
 * @param {number} K - Strike price of the option.
 * @param {number} T - Time to expiration of the option (in years).
 * @param {number} r - Risk-free interest rate (annualized, as a decimal).
 * @param {number} sigma - Volatility of the underlying asset (annualized, as a decimal).
 * @param {number} q - Dividend yield (annualized, as a decimal).
 * @returns {number} The theoretical price of the put option.
 */
export function blackScholesPut(S, K, T, r, sigma, q) {
  if (T === 0) {
    return Math.max(0, K - S);
  }
  const d1 = (Math.log(S / K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  const N_neg_d1 = cdf(-d1);
  const N_neg_d2 = cdf(-d2);

  const putPrice = K * Math.exp(-r * T) * N_neg_d2 - S * Math.exp(-q * T) * N_neg_d1;
  return putPrice;
}

// Black-Scholes Vega Calculation
export function calculateBlackScholesVega(S, K, T, r, sigma, q) {
  // S: Current stock price
  // K: Strike price
  // T: Time to expiration (in years)
  // r: Risk-free interest rate (annualized)
  // sigma: Volatility (annualized)
  // q: Dividend yield (annualized, as a decimal)

  if (T <= 0 || sigma <= 0) return 0;

  var d1 = (Math.log(S / K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  
  // The normal probability density function (PDF) at d1
  var ndist_d1 = (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-d1 * d1 / 2);

  var vega = S * Math.exp(-q * T) * Math.sqrt(T) * ndist_d1;

  return vega;
}

// Black-Scholes Delta Calculation
export function calculateBlackScholesDelta(S, K, T, r, sigma, q, optionType) {
  if (T <= 0 || sigma <= 0) {
    if (optionType === 'call') return S > K ? 1 : 0;
    return S < K ? -1 : 0;
  }

  var d1 = (Math.log(S / K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  
  if (optionType === 'call') {
    return Math.exp(-q * T) * cdf(d1);
  } else {
    return -Math.exp(-q * T) * cdf(-d1);
  }
}

// Black-Scholes Gamma Calculation
export function calculateBlackScholesGamma(S, K, T, r, sigma, q) {
  if (T <= 0 || sigma <= 0) return 0;

  var d1 = (Math.log(S / K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  var ndist_d1 = (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-d1 * d1 / 2);

  return (Math.exp(-q * T) * ndist_d1) / (S * sigma * Math.sqrt(T));
}

// Black-Scholes Theta Calculation
export function calculateBlackScholesTheta(S, K, T, r, sigma, q, optionType) {
  if (T <= 0 || sigma <= 0) return 0;

  const d1 = (Math.log(S / K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);
  const ndist_d1 = (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-d1 * d1 / 2);
  const term1 = -(S * Math.exp(-q * T) * ndist_d1 * sigma) / (2 * Math.sqrt(T));
  
  if (optionType === 'call') {
    const term2 = -r * K * Math.exp(-r * T) * cdf(d2);
    const term3 = q * S * Math.exp(-q * T) * cdf(d1);
    return (term1 + term2 + term3) / 365; // Return daily theta
  } else {
    const term2 = r * K * Math.exp(-r * T) * cdf(-d2);
    const term3 = -q * S * Math.exp(-q * T) * cdf(-d1);
    return (term1 + term2 + term3) / 365; // Return daily theta
  }
}

/**
 * Calculates the implied volatility of an option using the Newton-Raphson method.
 * @param {number} marketPrice - The market price of the option.
 * @param {number} S - Current price of the underlying asset.
 * @param {number} K - Strike price of the option.
 * @param {number} T - Time to expiration of the option (in years).
 * @param {number} r - Risk-free interest rate (annualized, as a decimal).
 * @param {number} q - Dividend yield (annualized, as a decimal).
 * @param {string} optionType - 'call' or 'put'.
 * @returns {number} The implied volatility.
 */
export function calculateImpliedVolatility(marketPrice, S, K, T, r, q, optionType) {
  const MAX_ITERATIONS = 100;
  const TOLERANCE = 1e-5;

  let sigma = 0.5; // Initial guess

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    let price;
    if (optionType === 'call') {
      price = blackScholesCall(S, K, T, r, sigma, q);
    } else {
      price = blackScholesPut(S, K, T, r, sigma, q);
    }

    const vega = calculateBlackScholesVega(S, K, T, r, sigma, q);
    const diff = price - marketPrice;

    if (Math.abs(diff) < TOLERANCE) {
      return sigma;
    }

    if (Math.abs(vega) < 1e-10) {
      break;
    }

    sigma = sigma - diff / vega;
  }

  return sigma; // Return the last calculated value if MAX_ITERATIONS is reached
}

export function calculatePL(optionRequest) {
  const { interest, dividendRatio, startPrice, endPrice, optionCombos, valuationDate, volatilityChange = 0 } = optionRequest;

  const results = [];

  for (const combo of optionCombos) {
    const pls = [];
    for (let S = startPrice; S <= endPrice; S++) {
      let comboPL = 0;
      for (const option of combo.stockOptions) {
        const { strikePrice, expiryDate, type, optionPrice, contract, impliedVolatility } = option;
        const timeToExpirationMs = new Date(expiryDate) - new Date(valuationDate);
        const T = Math.max(0, timeToExpirationMs / (1000 * 60 * 60 * 24 * 365));
        let blackScholesPrice = 0;
        const baseIv = Number.isFinite(Number(impliedVolatility)) ? Number(impliedVolatility) : 0;
        const sigma = Math.max(0.0001, baseIv + volatilityChange);
        if (type === 1) { // Call
          blackScholesPrice = blackScholesCall(S, strikePrice, T, interest, sigma, dividendRatio);
        } else if (type === 2) { // Put
          blackScholesPrice = blackScholesPut(S, strikePrice, T, interest, sigma, dividendRatio);
        } else { // Stock
          blackScholesPrice = S;
        }

        //console.log(`S: ${S}, K: ${strikePrice}, T: ${T.toFixed(4)}, Type: ${type}, BS Price: ${blackScholesPrice.toFixed(4)}, Option Price: ${optionPrice}, Contract: ${contract}`);

        const itemContract =  type === 0 ? contract : contract * 100; // For option, contract * 100
        //console.log(`profit: ${itemContract * (blackScholesPrice - optionPrice)}`);
        comboPL += (blackScholesPrice - optionPrice) * itemContract;
      }
      pls.push({ stockPrice: S, profit: comboPL });
    }
    results.push({ title: combo.title, pLs: pls });
  }

  return results;
}

export function calculateComboSnapshot(optionRequest, combo, stockPrice) {
  const { interest, dividendRatio, valuationDate, volatilityChange = 0 } = optionRequest;
  const snapshot = { profit: 0, delta: 0, gamma: 0, theta: 0, vega: 0 };

  if (!combo || !combo.stockOptions) {
    return snapshot;
  }

  for (const option of combo.stockOptions) {
    const { strikePrice, expiryDate, type, optionPrice, contract, impliedVolatility } = option;

    if (type === 0) {
      const shares = Number(contract) || 0;
      snapshot.profit += (stockPrice - optionPrice) * shares;
      snapshot.delta += shares;
      continue;
    }

    const timeToExpirationMs = new Date(expiryDate) - new Date(valuationDate);
    const T = Math.max(0, timeToExpirationMs / (1000 * 60 * 60 * 24 * 365));
    const baseIv = Number.isFinite(Number(impliedVolatility)) ? Number(impliedVolatility) : 0;
    const sigma = Math.max(0.0001, baseIv + volatilityChange);
    const optionType = type === 1 ? 'call' : 'put';
    const positionSize = (Number(contract) || 0) * 100;

    const theoreticalPrice = type === 1
      ? blackScholesCall(stockPrice, strikePrice, T, interest, sigma, dividendRatio)
      : blackScholesPut(stockPrice, strikePrice, T, interest, sigma, dividendRatio);

    snapshot.profit += (theoreticalPrice - optionPrice) * positionSize;
    snapshot.delta += calculateBlackScholesDelta(stockPrice, strikePrice, T, interest, sigma, dividendRatio, optionType) * positionSize;
    snapshot.gamma += calculateBlackScholesGamma(stockPrice, strikePrice, T, interest, sigma, dividendRatio) * positionSize;
    snapshot.theta += calculateBlackScholesTheta(stockPrice, strikePrice, T, interest, sigma, dividendRatio, optionType) * positionSize;
    snapshot.vega += calculateBlackScholesVega(stockPrice, strikePrice, T, interest, sigma, dividendRatio) * 0.01 * positionSize;
  }

  return snapshot;
}

/**
 * Calculates the average implied volatility from the option request data.
 * @param {object} optionRequest - The option request object containing option combos.
 * @returns {number} The average implied volatility, or 0 if no implied volatilities are found.
 */
export function calculateAverageImpliedVolatility(optionRequest) {
  let totalVolatility = 0;
  let count = 0;

  if (optionRequest && optionRequest.optionCombos) {
    for (const combo of optionRequest.optionCombos) {
      if (combo.stockOptions) {
        for (const option of combo.stockOptions) {
          if (typeof option.impliedVolatility === 'number') {
            totalVolatility += option.impliedVolatility;
            count++;
          }
        }
      }
    }
  }

  return count > 0 ? totalVolatility / count : 0;
}

export function daysBetween(dateA, dateB, { fractional = false } = {}) {
  const a = new Date(dateA);
  const b = new Date(dateB);
  const msPerDay = 24 * 60 * 60 * 1000;
  if (fractional) return (b - a) / msPerDay;
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((utcB - utcA) / msPerDay);
}

export function isValidDate(dateString) {
  const regex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const parts = dateString.match(regex);
  if (!parts) {
    return false;
  }
  const year = parseInt(parts[1], 10);
  const month = parseInt(parts[2], 10);


  if (year < 1000 || year > 9999 || month === 0 || month > 12) {
    return false;
  }

  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date) && date.getFullYear() === year;
}
